import { GitDiff } from "@phosphor-icons/react";

import {
  type ProjectFeedEvent,
  type ProjectVersionDiffPayload,
} from "../../../services/askPunttProjectFeed.types";

/**
 * Version-diff card (§12 / P4) — rendered when a new file version triggers an
 * auto re-review. Shows the from→to version transition, the changed regions
 * Puntt detected, and an optional deep link into the review run.
 */
export function FeedVersionDiffCard({
  event,
  onOpenReview,
}: {
  event: ProjectFeedEvent;
  onOpenReview?: (reviewRunId: string) => void;
}) {
  const payload = (event.payload ?? {}) as ProjectVersionDiffPayload;
  const fileName = payload.fileName ?? "File";
  const changes = payload.changes ?? [];
  const titleId = `feed-version-diff-title-${event._id}`;
  const hasRange = payload.fromVersion != null && payload.toVersion != null;

  return (
    <section
      className="rounded-xl border border-puntt-neutral-gray-5 bg-base-white p-3.5"
      data-testid="project-feed-version-diff-card"
      aria-labelledby={titleId}
    >
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-puntt-neutral-gray-3 text-puntt-neutral-gray-11"
        >
          <GitDiff size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <h3
            id={titleId}
            className="truncate text-[13px] font-semibold text-puntt-neutral-gray-12"
          >
            {fileName}
          </h3>
          {hasRange && (
            <p
              className="font-mono text-[11px] text-puntt-neutral-gray-11"
              data-testid="project-feed-version-diff-range"
            >
              v{payload.fromVersion} → v{payload.toVersion}
            </p>
          )}
        </div>
      </div>
      {changes.length > 0 && (
        <ul
          className="mt-2 list-disc space-y-1 pl-7 text-[12.5px] leading-relaxed text-puntt-neutral-gray-12"
          data-testid="project-feed-version-diff-changes"
        >
          {changes.map((c, i) => (
            <li key={i}>{c}</li>
          ))}
        </ul>
      )}
      {payload.reviewRunId && onOpenReview && (
        <div className="mt-3">
          <button
            type="button"
            data-testid="project-feed-version-diff-open-review"
            aria-label="Open re-review run"
            onClick={() => onOpenReview(payload.reviewRunId!)}
            className="rounded-md border border-puntt-neutral-gray-6 px-2.5 py-1 text-[12px] font-medium text-puntt-neutral-gray-12 hover:bg-puntt-neutral-gray-3"
          >
            View re-review
          </button>
        </div>
      )}
    </section>
  );
}
